import * as React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Eye, History, ShoppingCart, Trash2 } from 'lucide-react'
import {
  useDeleteWatcher, useRegistrationBudget, useRegistrationOrders, useUpdateWatcher, useWatchers,
} from '@/lib/queries'
import { CreateOrderDialog } from '@/features/registration/CreateOrderDialog'
import { useAuth } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip } from '@/components/ui/tooltip'
import { FilterSelect } from '@/components/ui/filter-select'
import { EmptyState, ErrorState, LoadingState, PartialState } from '@/components/ui/states'
import { FreshnessCell, StatusBadge } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { DomainDetailDrawer } from '@/components/layout/DomainDetailDrawer'
import { ObservationHistory } from '@/features/rate-limit/ObservationHistory'
import { formatDateTimeSeconds, formatLimit, formatNumber } from '@/lib/utils'

/**
 * The gap between the last confirmation and the next check — the cadence this
 * domain is actually getting.
 *
 * Shown instead of the scheduling tier because a watched domain is not governed
 * by the tier. The tier is the platform's own ranking of a name, used to decide
 * who goes first when capacity is short; putting it in this column implied it
 * set the cadence, which is the opposite of how a watch works.
 */
function checkInterval(w: { last_resolved_at?: string; next_check_at?: string }): string {
  if (!w.last_resolved_at || !w.next_check_at) return '—'
  const secs = Math.round((Date.parse(w.next_check_at) - Date.parse(w.last_resolved_at)) / 1000)
  if (secs <= 0) return '—'
  if (secs < 60) return `${secs} 秒`
  if (secs < 3600) return `${Math.round(secs / 60)} 分钟`
  if (secs < 86400) return `${Math.round(secs / 3600)} 小时`
  return `${Math.round(secs / 86400)} 天`
}

const WATCH_STATUS_OPTIONS = [
  { value: 'active', label: '监控中', description: '正在按计划持续检查' },
  { value: 'released', label: '可注册', description: '已经外部渠道确认可注册' },
  { value: 'observed_registered', label: '已被注册', description: '观察到域名已被他人注册' },
  { value: 'paused', label: '已暂停', description: '暂时不执行检查' },
  { value: 'archived', label: '已归档', description: '已结束监控并保留历史' },
]

const WATCH_STATUS_LABELS = Object.fromEntries(
  WATCH_STATUS_OPTIONS.map((option) => [option.value, option.label]),
) as Record<string, string>

export default function MonitoringPage() {
  // Status is a list filter, just like the domain library's current-status
  // filter. Keeping it in the URL still makes filtered views shareable without
  // turning each outcome into a separate navigation page.
  const [params, setParams] = useSearchParams()
  const requestedStatus = params.get('status') ?? ''
  const statusFilter = WATCH_STATUS_LABELS[requestedStatus] ? requestedStatus : undefined
  const [historyFor, setHistoryFor] = React.useState<{ id: string; domain: string } | null>(null)
  // The domain a registration order is being created for, straight from this row.
  const [orderFor, setOrderFor] = React.useState<string | null>(null)
  // The order dialog needs the caps to show what is left today; without them it
  // would be asking for a price ceiling with nothing to compare it against.
  const { data: budgetData } = useRegistrationBudget()
  const budget = budgetData?.budget

  // Domains that already have a live order. Fetched so the button can be absent
  // rather than present-and-doomed: one live order per domain is a rule the
  // server enforces, and a control that exists only to explain why it cannot
  // work is a worse answer than not offering it.
  const { data: orderData } = useRegistrationOrders()
  const ordered = React.useMemo(
    () => new Set(
      (orderData?.orders ?? [])
        .filter((o) => ['queued', 'registering', 'verifying'].includes(o.status))
        .map((o) => o.domain_ascii),
    ),
    [orderData],
  )
  const { data, isLoading, error, refetch } = useWatchers(statusFilter)
  const update = useUpdateWatcher()
  const remove = useDeleteWatcher()
  const toast = useToast()
  const { user } = useAuth()
  const [detailId, setDetailId] = React.useState<string | null>(null)

  const limits = data?.limits
  const active = data?.counts?.active ?? 0
  const total = Object.values(data?.counts ?? {}).reduce((sum, count) => sum + count, 0)

  return (
    <>
      <PageHeader
        title="监控"
        description={
          <span>
            {statusFilter
              ? `当前筛选 ${formatNumber(data?.counts?.[statusFilter] ?? 0)} 个域名`
              : `共 ${formatNumber(total)} 个域名`}
            {limits && (
              <> · 监控中 {formatNumber(active)} / {formatLimit(limits.max_active_watchers)} 个槽位</>
            )}
            {limits && limits.notification_delay_seconds > 0 && (
              <> · 当前套餐关键通知延迟约 {Math.round(limits.notification_delay_seconds / 60)} 分钟</>
            )}
          </span>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterSelect
          label="监控状态"
          options={WATCH_STATUS_OPTIONS.map((option) => ({
            ...option,
            badge: formatNumber(data?.counts?.[option.value] ?? 0),
          }))}
          selected={statusFilter ? [statusFilter] : []}
          onToggle={(value) => {
            const next = new URLSearchParams(params)
            if (statusFilter === value) next.delete('status')
            else next.set('status', value)
            setParams(next, { replace: true })
          }}
          onClear={() => {
            const next = new URLSearchParams(params)
            next.delete('status')
            setParams(next, { replace: true })
          }}
        />
      </div>

      {limits && limits.notification_delay_seconds > 0 && (
        <PartialState
          className="mb-4"
          message={`免费层的关键通知会延迟约 ${Math.round(limits.notification_delay_seconds / 60)} 分钟送达。域名释放是分秒级竞争，升级套餐可获得实时通知。`}
        />
      )}

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <LoadingState rows={6} />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyState
          icon={Eye}
          title={statusFilter ? `没有${WATCH_STATUS_LABELS[statusFilter]}的域名` : '还没有监控域名'}
          description={
            !statusFilter || statusFilter === 'active'
              ? '在域名库或域名详情中点击「加入监控」，即可跟踪其生命周期变化。'
              : '调整监控状态筛选，查看其他域名。'
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <div className="scroll-x">
            <table className="w-full min-w-[980px] text-sm">
              <caption className="sr-only">监控列表</caption>
              <thead className="border-b bg-muted/40">
                <tr>
                  {['域名', '释放方式', '优先级', '当前状态', '最后确认', '下次检查', '检查间隔', '注册后处理', '操作'].map((h) => (
                    <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data!.items.map((w) => (
                  <tr key={w.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setDetailId(w.domain_id)}
                        className="font-mono text-xs text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {w.domain}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      {w.status === 'observed_registered' ? (
                        <Tooltip content="未捕获到开放窗口，随后观察到域名已被重新注册">
                          <Badge variant="warning">抢注</Badge>
                        </Tooltip>
                      ) : w.status === 'released' ? (
                        <Tooltip content="平台捕获到了域名进入可注册状态">
                          <Badge variant="success">正常</Badge>
                        </Tooltip>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={w.priority}
                        onChange={async (e) => {
                          try {
                            await update.mutateAsync({ id: w.id, body: { priority: e.target.value } })
                            toast.success('优先级已更新')
                          } catch (err) {
                            toast.error('更新失败', err instanceof ApiError ? err.message : undefined)
                          }
                        }}
                        className="h-7 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`${w.domain} 的监控优先级`}
                      >
                        <option value="normal">普通</option>
                        <option value="high">较高</option>
                        <option value="critical">关键</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge
                        status={w.current_status}
                        source={w.status_source}
                        confirmedAt={w.last_resolved_at}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <FreshnessCell lastResolvedAt={w.last_resolved_at} watched timezone={user?.timezone} />
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {formatDateTimeSeconds(w.next_check_at, user?.timezone)}
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{checkInterval(w)}</td>
                    <td className="px-3 py-2">
                      <select
                        value={w.post_registration_action}
                        onChange={async (e) => {
                          try {
                            await update.mutateAsync({ id: w.id, body: { post_registration_action: e.target.value } })
                            toast.success('已更新注册后处理方式')
                          } catch (err) {
                            toast.error('更新失败', err instanceof ApiError ? err.message : undefined)
                          }
                        }}
                        className="h-7 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`${w.domain} 被注册后的处理方式`}
                      >
                        <option value="keep_watching">继续监控下一周期</option>
                        <option value="auto_archive">自动归档</option>
                        <option value="acquired">标记为已获取</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {w.status === 'released' && (
                        <Button asChild size="sm" variant="outline" className="mr-1">
                          <a
                            href={`https://www.spaceship.com/domain-search/?query=${w.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            去注册
                          </a>
                        </Button>
                      )}
                      {ordered.has(w.domain) ? (
                        // Already ordered: no button at all. The badge is a link
                        // rather than a dead label, because "it already has one"
                        // immediately raises "which one, and how is it doing".
                        <Tooltip content="该域名已有进行中的注册单，点击查看">
                          <Link to="/registration" className="mr-1 inline-flex align-middle">
                            <Badge variant="secondary">已下单</Badge>
                          </Link>
                        </Tooltip>
                      ) : (
                        <Tooltip content={
                          budget
                            ? '为该域名建立自动注册单：确认可注册的那一刻自动下单'
                            : '正在读取额度设置…'
                        }>
                          <Button
                            variant="ghost" size="icon-sm"
                            disabled={!budget}
                            onClick={() => setOrderFor(w.domain)}
                            aria-label={`为 ${w.domain} 建立注册单`}
                          >
                            <ShoppingCart className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip content="查询记录：每次请求的时间、耗时、HTTP 状态，可筛选被限流的">
                        <Button
                          variant="ghost" size="icon-sm"
                          onClick={() => setHistoryFor({ id: w.domain_id, domain: w.domain })}
                          aria-label={`${w.domain} 的查询记录`}
                        >
                          <History className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </Tooltip>
                      <Tooltip content="取消监控（归档，不删除域名与历史）">
                        <Button
                          variant="ghost" size="icon-sm"
                          onClick={async () => {
                            try {
                              await remove.mutateAsync(w.id)
                              toast.success('已取消监控')
                            } catch (err) {
                              toast.error('操作失败', err instanceof ApiError ? err.message : undefined)
                            }
                          }}
                          aria-label={`取消监控 ${w.domain}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                        </Button>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DomainDetailDrawer domainId={detailId} onClose={() => setDetailId(null)} />
      <ObservationHistory
        domainId={historyFor?.id ?? null}
        domain={historyFor?.domain ?? ''}
        onClose={() => setHistoryFor(null)}
      />
      {orderFor && budget && (
        <CreateOrderDialog
          budget={budget}
          lockedDomain={orderFor}
          open
          onOpenChange={(o) => { if (!o) setOrderFor(null) }}
        />
      )}
    </>
  )
}
